import { createSelector } from 'reselect'
import { EmptyArray, groupBy } from 'lib/array/arrayUtils'
import getCheckoutLoyaltyPointsEarnCalculationView from 'checkout/selectors/loyalty/pointsCalculation/getCheckoutLoyaltyPointsEarnCalculationView'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import getBedbankItems from 'checkout/selectors/view/items/getBedbankItems'
import { getTourV2CheckoutItems } from 'checkout/selectors/view/accommodation'
import { buildMergedOrderLoyaltyPendingPointsPayload } from 'loyalty/selectors/checkout/order/buildMergedOrderLoyaltyPendingPointsPayload'
import { CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2 } from 'constants/checkout'

function buildOrderLoyaltyPendingPointsPayload(
  earnableItem: App.Checkout.LoyaltyPointsEarnItem,
  isPostPurchase: boolean,
): App.OrderLoyaltyPendingPointsPayload {
  return {
    transactionKey: earnableItem.itemTransactionKey,
    points: earnableItem.totals.earnableBasePoints,
    bonusPoints: earnableItem.totals.earnableBonusPoints,
    statusCredits: earnableItem.totals.earnableStatusCredits,
    bonusStatusCredits: earnableItem.totals.earnableBonusStatusCredits,
    appliedCustomRuleIds: earnableItem.totals.appliedRuleIds,
    // Only include itemId at top level for post-purchase flows (references existing order item)
    ...(earnableItem.orderItemId && { itemId: earnableItem.orderItemId }),
    metadata: {
      itemId: earnableItem.itemId,
      orderItemId: earnableItem.orderItemId,
      itemTransactionKey: earnableItem.itemTransactionKey,
      loyaltyGroupKey: earnableItem.loyaltyGroupKey,
      title: earnableItem.title,
      loyaltyProductType: earnableItem.loyaltyProductType,
      itemType: earnableItem.itemType,
      totals: earnableItem.totals,
      pointsEarnCalculation: earnableItem.pointsEarnCalculation,
      source: 'Web',
      purchaseMode: isPostPurchase ? 'post-purchase' : 'order-creation',
    },
  }
}

const getOrderLoyaltyPendingPoints = createSelector(
  (state: App.State) => state.auth.account.memberId ?? '',
  (state: App.State) => getCheckoutLoyaltyPointsEarnCalculationView(state),
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getTourV2CheckoutItems(state),
  (
    customerId,
    pointsEarnCalculationView,
    postPurchaseMode,
    bedbankItems,
    tourV2Items,
  ): Array<App.OrderLoyaltyPendingPointsPayload> => {
    if (
      !customerId ||
      !pointsEarnCalculationView.hasRequiredData
    ) return EmptyArray

    const pendingPointItems: Array<App.OrderLoyaltyPendingPointsPayload> = []
    const itemsWithGroupKey = pointsEarnCalculationView.data!.earnableItems.filter(item => item.loyaltyGroupKey)
    const earnableItemsByGroupKey = groupBy(itemsWithGroupKey, item => item.loyaltyGroupKey)
    const processedGroupKeys = new Set<string>()

    pointsEarnCalculationView.data!.earnableItems.forEach((earnableItem) => {
      switch (earnableItem.loyaltyProductType) {
        /**
         * @NOTE bedbank items of the same session ID are treated as individual items in CP but as the same item
         * with multiple rooms in svc-order. For this reason we need to merge the pending points items in this case.
         * We deduplicate by loyaltyGroupKey to avoid pushing the same merged entry multiple times (once per room),
         * which would cause a unique constraint violation in svc-lux-loyalty.
         */
        case 'hotel_lpp_eps':
          // accommodationV2 bedbank rooms are one svc-order item each, so they aren't merged by session
          if (earnableItem.itemType === CHECKOUT_ITEM_TYPE_ACCOMMODATION_V2) {
            pendingPointItems.push(buildOrderLoyaltyPendingPointsPayload(earnableItem, !!postPurchaseMode))
            break
          }
          if (!earnableItem.loyaltyGroupKey || processedGroupKeys.has(earnableItem.loyaltyGroupKey)) return
          processedGroupKeys.add(earnableItem.loyaltyGroupKey)

          const bedbankItem = bedbankItems.find(item => item.sessionId === earnableItem.loyaltyGroupKey)

          if (!bedbankItem) return

          const groupedEarnableItems = earnableItemsByGroupKey.get(earnableItem.loyaltyGroupKey)

          if (!groupedEarnableItems) return

          pendingPointItems.push(
            buildMergedOrderLoyaltyPendingPointsPayload(
              groupedEarnableItems,
              earnableItem,
              bedbankItem.transactionKey,
              !!postPurchaseMode,
            ),
          )
          break
        /**
         * @NOTE multi-room tour items share one departure in svc-order but are separate earnable
         * items in checkout. Merge pending points by departure (loyaltyGroupKey) and use the first
         * cart room's transactionKey to match formatTourV2ItemGroup / order creation.
         */
        case 'le_tour':
        case 'tour_bank':
        case 'trusted_partner_tour':
          if (!earnableItem.loyaltyGroupKey || processedGroupKeys.has(earnableItem.loyaltyGroupKey)) return
          processedGroupKeys.add(earnableItem.loyaltyGroupKey)

          const groupedTourEarnableItems = earnableItemsByGroupKey.get(earnableItem.loyaltyGroupKey)

          if (!groupedTourEarnableItems?.length) return

          const primaryTourCartItem = tourV2Items.find(
            item => item.purchasableOption.fkDepartureId === earnableItem.loyaltyGroupKey,
          )

          if (!primaryTourCartItem) return

          const tourTransactionKey = primaryTourCartItem.transactionKey

          pendingPointItems.push(
            buildMergedOrderLoyaltyPendingPointsPayload(
              groupedTourEarnableItems,
              earnableItem,
              tourTransactionKey,
              !!postPurchaseMode,
            ),
          )
          break
        default:
          pendingPointItems.push(buildOrderLoyaltyPendingPointsPayload(earnableItem, !!postPurchaseMode))
          break
      }
    })

    return pendingPointItems
  },
)

export default getOrderLoyaltyPendingPoints
